import {
  useActivePractice,
  useDispatch,
  useFileNotesFromProjectId,
  useKeypointsFromProjectId,
  useNotesFromProjectId,
  usePracticeUsers,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { mapToKey } from '@witmetrics/utils/arrays';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import NotesField, {
  type NotesFieldProps,
} from '@/components/inputs/NotesField';
import KeypointField, {
  type KeypointFieldProps,
} from '@/components/KeypointField';
import NotesTimeline from '@/components/NotesTimeline';
import FileOverlay from '@/overlays/FileOverlay';
import TimelineMenu from './TimelineMenu';
import TimelineToggle from './TimelineToggle';
import {
  classes,
  createProjectKeypoint,
  createProjectNote,
  useTimelineSettings,
} from './utils';

export default function TimelineSection() {
  const dispatch = useDispatch();
  const { onApiError, activeProjectId } = useAppState();
  const activePractice = useActivePractice();
  const keypoints = useKeypointsFromProjectId(activeProjectId);
  const notes = useNotesFromProjectId(activeProjectId);
  const fileNotes = useFileNotesFromProjectId(activeProjectId);
  const practiceUsers = usePracticeUsers();
  const { addLoadedFiles } = useLoadedFiles();
  const [timelineSettings, setTimelineSettings] = useTimelineSettings();
  const [activeFileId, setActiveFileId, { clear: clearFileId }] = useId();
  const [isNewNoteShown, toggleNewNote] = useToggle();
  const [isNewKeypointShown, toggleNewKeypoint] = useToggle();
  const [isSavingNote, toggleSavingNote] = useToggle();
  const [isSavingKeypoint, toggleSavingKeypoint] = useToggle();

  const handleSaveNote: NotesFieldProps['onSave'] = async (
    message,
    selectedFiles
  ) => {
    try {
      toggleSavingNote(true);
      const { projectNote, attachments } = await createProjectNote(
        activeProjectId!,
        message,
        selectedFiles
      );
      addLoadedFiles(
        attachments.reduce(
          (a, b) => ({
            ...a,
            [b.noteFile.fileId]: b.selectedFile.source,
            [`preview-${b.noteFile.file.versions[0].id}`]:
              b.selectedFile.previewSource,
          }),
          {}
        )
      );
      dispatch.addProjectNotes([projectNote]);
      dispatch.addNoteFiles(mapToKey(attachments, 'noteFile'));
      toggleSavingNote(false);
      toggleNewNote(false);
    } catch (err) {
      toggleSavingNote(false);
      onApiError(err, 'Error saving note');
    }
  };

  const handleSaveKeypoint: KeypointFieldProps['onSave'] = async ({
    keypointTypeId,
    startsAt,
    message,
  }) => {
    try {
      if (!activePractice) return console.warn('No active practice'); // TODO
      toggleSavingKeypoint(true);
      dispatch.addProjectKeypoints([
        await createProjectKeypoint({
          projectId: activeProjectId!,
          practiceId: activePractice!.id,
          keypointTypeId,
          startsAt,
          message,
        }),
      ]);
      toggleSavingKeypoint(false);
      toggleNewKeypoint(false);
    } catch (err) {
      toggleSavingKeypoint(false);
      onApiError(err, 'Error saving keypoint');
    }
  };

  const handleNewNote = () => {
    toggleNewNote(true);
    toggleNewKeypoint(false);
  };

  const handleNewKeypoint = () => {
    toggleNewKeypoint(true);
    toggleNewNote(false);
  };

  const renderContent = () => {
    if (isEmpty([...notes, ...fileNotes, ...keypoints])) {
      return (
        <div className={classes.message}>
          No notes or keypoints for current case.
        </div>
      );
    }
    return (
      <NotesTimeline
        className={classes.timeline}
        variant="aurora"
        timelineSettings={timelineSettings || 'ALL'}
        keypoints={keypoints}
        notes={notes}
        fileNotes={fileNotes}
        onOpenFile={setActiveFileId}
      />
    );
  };

  if (!activeProjectId) return null;

  return (
    <>
      {activeFileId && (
        <FileOverlay fileId={activeFileId} onClose={clearFileId} />
      )}
      <div className={classes.column}>
        <div className={classes.titleBlock}>
          <h4 className={classes.title}>Timeline</h4>
          <div className={classes.controls}>
            <TimelineToggle
              value={timelineSettings!}
              onChange={setTimelineSettings}
            />
            <TimelineMenu
              onAddNote={handleNewNote}
              onAddKeypoint={handleNewKeypoint}
            />
          </div>
        </div>
        {isNewNoteShown && (
          <div className={classes.fieldWrapper}>
            <NotesField
              allowAttachments
              isSaving={isSavingNote}
              className={classes.field}
              placeholder="Add a note, @mention a colleague..."
              users={mapToKey(practiceUsers, 'user')}
              onSave={handleSaveNote}
              onCancel={() => toggleNewNote(false)}
            />
          </div>
        )}
        {isNewKeypointShown && (
          <div className={classes.fieldWrapper}>
            <KeypointField
              isSaving={isSavingKeypoint}
              className={classes.field}
              onSave={handleSaveKeypoint}
              onCancel={() => toggleNewKeypoint(false)}
            />
          </div>
        )}
        {renderContent()}
      </div>
    </>
  );
}
